import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

/**
 * Safely resolves an i18n key expected to be an array. Guarantees an array
 * return value even when:
 *  - i18n hasn't finished initializing (SSR before resources hydrate)
 *  - the key is missing in the active language
 *  - the key resolves to a string (i18next's default missing-key behavior)
 */
export function tArray<T = string>(t: TFunction, key: string, fallback: T[] = []): T[] {
  const value = t(key, { returnObjects: true, defaultValue: fallback as unknown as string });
  return Array.isArray(value) ? (value as T[]) : fallback;
}

/**
 * Maps an i18next language code to a BCP-47 locale used by Intl APIs.
 * - pt -> pt-BR
 * - en -> en-US
 * - es -> es-ES
 */
export function languageToLocale(lang: string | undefined): string {
  const base = (lang ?? "pt").toLowerCase().split("-")[0];
  switch (base) {
    case "en":
      return "en-US";
    case "es":
      return "es-ES";
    case "pt":
    default:
      return "pt-BR";
  }
}

export function formatMoney(
  value: number | null | undefined,
  currency: string | null | undefined,
  locale: string,
  options?: Intl.NumberFormatOptions,
): string {
  const amount = Number(value ?? 0);
  const cur = (currency ?? "USD").toUpperCase();
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency: cur, ...options }).format(
      amount,
    );
  } catch {
    return `${cur} ${amount.toFixed(2)}`;
  }
}

export function formatDate(
  value: string | number | Date | null | undefined,
  locale: string,
  options: Intl.DateTimeFormatOptions = { year: "numeric", month: "short", day: "2-digit" },
): string {
  if (!value) return "";
  // Date-only strings ("YYYY-MM-DD") must be rendered as the same calendar day
  // regardless of the viewer's timezone. Without this, parsing as UTC midnight
  // and formatting in a non-UTC zone shifts the displayed day by ±1.
  const isDateOnly = typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const opts: Intl.DateTimeFormatOptions = isDateOnly ? { ...options, timeZone: "UTC" } : options;
  return new Intl.DateTimeFormat(locale, opts).format(d);
}

export function formatDateTime(
  value: string | number | Date | null | undefined,
  locale: string,
): string {
  return formatDate(value, locale, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatNumber(
  value: number | null | undefined,
  locale: string,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(locale, options).format(Number(value ?? 0));
}

/**
 * Convenience hook: returns locale + curried formatters using current i18n language.
 */
export function useLocaleFormat() {
  const { i18n } = useTranslation();
  const locale = languageToLocale(i18n.language);
  return {
    locale,
    formatMoney: (
      v: number | null | undefined,
      currency: string | null | undefined,
      options?: Intl.NumberFormatOptions,
    ) => formatMoney(v, currency, locale, options),
    formatDate: (v: string | number | Date | null | undefined, opts?: Intl.DateTimeFormatOptions) =>
      formatDate(v, locale, opts),
    formatDateTime: (v: string | number | Date | null | undefined) => formatDateTime(v, locale),
    formatNumber: (v: number | null | undefined, opts?: Intl.NumberFormatOptions) =>
      formatNumber(v, locale, opts),
  };
}
